import { type ColDef, type GetRowIdParams } from 'ag-grid-community';
import { type CustomCellRendererProps } from 'ag-grid-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import { GroupsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Accordion,
	Button,
	Card,
	DataGrid,
	Empty,
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	Label,
	Paragraph,
	UserRow,
	tokens,
} from '@treams/treams-ui';

import NominateSuccessorModal from './NominateSuccessorModal';
import { SuccessorNominationsPopover } from './SuccessorNominationsPopover';
import { type JobTitleSuccessorsRow, buildJobTitleSuccessors } from './jobTitleSuccessors';
import { READINESS_OPTIONS, readinessLabelKey } from './successorReadiness';

type Nomination = graphql.JobPositionSuccessorFragment;
type Holder = graphql.JobTitleSuccessorsQuery['jobTitleSuccessors'][number]['holders'][number];
type BoardRow = JobTitleSuccessorsRow<Nomination, Holder>;

const UserLink = styled(Link)<{ $inactive?: boolean; $fill?: boolean }>`
	text-decoration: none;
	color: ${(props) => (props.$inactive ? tokens.error.text : 'inherit')};
	display: block;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	${(props) => props.$fill && 'flex: 1;'}

	p {
		color: ${(props) => (props.$inactive ? tokens.error.text : undefined)};
	}
`;

const candidateCellStyle = {
	display: 'block',
	whiteSpace: 'normal' as const,
	lineHeight: 'normal',
	paddingTop: '10px',
	paddingBottom: '10px',
};

// Alpine auto-height keeps the body at least 150px, so one job title still
// leaves empty space under the row.
const SuccessionGrid = styled.div`
	min-width: 0;

	&& .ag-layout-auto-height .ag-center-cols-viewport,
	&& .ag-layout-auto-height .ag-center-cols-container {
		min-height: 0;
	}

	/* The cell value is a flex item. Without a zero min-width it sizes to the
	   candidate text and the mention count gets clipped at the column edge. */
	&& .ag-cell-wrapper,
	&& .ag-cell-value {
		min-width: 0;
		max-width: 100%;
	}

	&& .ag-cell-value {
		flex: 1;
		overflow: hidden;
	}
`;

function HoldersCell({ holders }: { holders: BoardRow['holders'] }) {
	const { t } = useTranslation();

	if (holders.length === 0) {
		return <Paragraph>—</Paragraph>;
	}

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.element} width="100%" minWidth={0}>
			{holders.map((holder) => {
				const inactive = holder.status !== graphql.UserStatus.Active;

				return (
					<UserLink key={holder.id} to={`/users/${holder.id}`} $inactive={inactive}>
						<UserRow
							truncate
							user={holder}
							avatarProps={{ size: 32 }}
							subtitle={inactive ? t('analytics.talentReview.succession.inactive') : undefined}
						/>
					</UserLink>
				);
			})}
		</FlexGap>
	);
}

function CandidatesCell({
	candidates,
	onWithdrawn,
}: {
	candidates: BoardRow['candidatesByReadiness'][graphql.SuccessorReadiness];
	onWithdrawn: () => void;
}) {
	const { t } = useTranslation();

	if (candidates.length === 0) {
		return <Paragraph>—</Paragraph>;
	}

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.element} width="100%" minWidth={0}>
			{candidates.map((candidate) => {
				const inactive = candidate.user.status !== graphql.UserStatus.Active;
				const subtitle = inactive
					? t('analytics.talentReview.succession.inactive')
					: candidate.currentJobTitle || undefined;

				return (
					<FlexGap
						key={candidate.id}
						gap={tokens.gap.element}
						alignItems="center"
						width="100%"
						minWidth={0}
					>
						<UserLink
							to={`/users/${candidate.user.id}`}
							$inactive={inactive}
							$fill
							title={subtitle}
						>
							<UserRow
								truncate
								user={candidate.user}
								avatarProps={{ size: 32 }}
								subtitle={subtitle}
							/>
						</UserLink>
						<SuccessorNominationsPopover
							nominations={candidate.nominations}
							onWithdrawn={onWithdrawn}
						/>
					</FlexGap>
				);
			})}
		</FlexGap>
	);
}

function SuccessionTable({ rows, onWithdrawn }: { rows: BoardRow[]; onWithdrawn: () => void }) {
	const { t } = useTranslation();

	const columnDefs = useMemo<ColDef<BoardRow>[]>(
		() => [
			{
				colId: 'position',
				headerName: t('analytics.talentReview.succession.modal.jobTitle'),
				flex: 1.2,
				minWidth: 180,
				sortable: true,
				valueGetter: ({ data }) => data?.title ?? '',
				cellRenderer: ({ data }: CustomCellRendererProps<BoardRow>) =>
					data ? <Label>{data.title}</Label> : null,
			},
			{
				colId: 'holder',
				headerName: t('analytics.talentReview.succession.column.currentHolder'),
				flex: 1.4,
				minWidth: 200,
				sortable: true,
				valueGetter: ({ data }) =>
					data?.holders.map((holder) => `${holder.firstName} ${holder.lastName}`).join(', ') ?? '',
				cellRenderer: ({ data }: CustomCellRendererProps<BoardRow>) =>
					data ? <HoldersCell holders={data.holders} /> : null,
			},
			...READINESS_OPTIONS.map((readiness) => ({
				colId: readiness,
				headerName: t(readinessLabelKey(readiness)),
				flex: 2,
				minWidth: 220,
				sortable: false,
				valueGetter: ({ data }) =>
					data?.candidatesByReadiness[readiness]
						.map((candidate) => `${candidate.user.firstName} ${candidate.user.lastName}`)
						.join(', ') ?? '',
				cellRenderer: ({ data }: CustomCellRendererProps<BoardRow>) =>
					data ? (
						<CandidatesCell
							candidates={data.candidatesByReadiness[readiness]}
							onWithdrawn={onWithdrawn}
						/>
					) : null,
			})),
		],
		[onWithdrawn, t],
	);

	return (
		<SuccessionGrid>
			<DataGrid<BoardRow>
				rowData={rows}
				columnDefs={columnDefs}
				getRowId={({ data }: GetRowIdParams<BoardRow>) => data.id}
				rowHeight={undefined}
				domLayout="autoHeight"
				defaultColDef={{
					autoHeight: true,
					wrapText: true,
					cellStyle: candidateCellStyle,
				}}
			/>
		</SuccessionGrid>
	);
}

/**
 * Job title succession: every key position as its own row, one column per
 * readiness, and the other positions behind a single collapsible.
 */
export function TalentReviewSuccessorsCard() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { isWorkspaceSuperuser, isAdministrator, isHRBusinessPartner } = useViewerRoles();
	const isHR = isWorkspaceSuperuser || isAdministrator || isHRBusinessPartner;
	const [modalOpen, setModalOpen] = useState(false);

	const { data, loading, error, refetch } = graphql.useJobTitleSuccessorsQuery();

	const { keyPositions, otherPositions } = useMemo(
		() => buildJobTitleSuccessors(data?.jobTitleSuccessors ?? [], localize, READINESS_OPTIONS),
		[data, localize],
	);

	const hasRows = keyPositions.length > 0 || otherPositions.length > 0;
	// A parent-department manager may see succession without being allowed to
	// nominate. Each job title carries that separate permission.
	const canNominate =
		!error &&
		(isHR ||
			(data?.jobTitleSuccessors ?? []).some((entry) => entry.jobTitle.viewerCanManageSuccession));

	return (
		<Card>
			<FlexGap gap={tokens.gap.group} alignItems="flex-start" justifyContent="space-between">
				<Box minWidth={0}>
					<Heading3 mb={2}>{t('analytics.talentReview.succession.keyPositions')}</Heading3>
					<Paragraph>{t('analytics.talentReview.succession.subtitle')}</Paragraph>
				</Box>
				{canNominate && (
					<Button
						small
						title={t('analytics.talentReview.succession.add')}
						onClick={() => setModalOpen(true)}
					/>
				)}
			</FlexGap>
			<Box mt={3} mx={-3} mb={-3} minWidth={0}>
				{loading && !data ? (
					<DataGrid.Skeleton columns={5} rows={4} />
				) : error ? (
					<Box px={3} pb={3}>
						{error.graphQLErrors[0] ? (
							<GraphQLErrorMessage error={error.graphQLErrors[0]} />
						) : (
							<Paragraph color="danger">
								{t('analytics.talentReview.succession.unauthorized')}
							</Paragraph>
						)}
					</Box>
				) : !hasRows ? (
					<Flex py={5} justifyContent="center">
						<Empty
							small
							icon={<GroupsIcon />}
							title={t('analytics.talentReview.succession.empty.title')}
							description={t('analytics.talentReview.succession.empty.description')}
						/>
					</Flex>
				) : (
					<>
						{keyPositions.length > 0 && (
							<SuccessionTable rows={keyPositions} onWithdrawn={() => refetch()} />
						)}
						{otherPositions.length > 0 && (
							<Box
								style={
									keyPositions.length > 0
										? { borderTop: `1px solid ${tokens.border.subtle}` }
										: undefined
								}
							>
								<Accordion
									small
									borderless
									noPadding
									title={t('analytics.talentReview.succession.otherPositions')}
									count={otherPositions.length}
									initialIsCollapsed={keyPositions.length > 0}
								>
									<SuccessionTable rows={otherPositions} onWithdrawn={() => refetch()} />
								</Accordion>
							</Box>
						)}
					</>
				)}
			</Box>
			{modalOpen && (
				<NominateSuccessorModal
					onClose={() => setModalOpen(false)}
					onNominated={() => {
						setModalOpen(false);
						refetch();
					}}
				/>
			)}
		</Card>
	);
}

TalentReviewSuccessorsCard.Skeleton = function TalentReviewSuccessorsCardSkeleton() {
	return (
		<Card>
			<FlexGap gap={tokens.gap.group} alignItems="flex-start" justifyContent="space-between">
				<Box>
					<Heading3.Skeleton width={220} />
					<Paragraph.Skeleton lines={1} mt={2} width={260} />
				</Box>
				<Button.Skeleton small />
			</FlexGap>
			<Box mt={3} mx={-3} mb={-3}>
				<DataGrid.Skeleton columns={5} rows={4} />
			</Box>
		</Card>
	);
};
